import type { CardPreviewDensity } from "@/components/shapes/card-previews";
import { GadgetCard } from "@/components/shapes/shared/card-visual-parts";
import { OPENED_PART } from "@/components/shapes/shared/object-frame";
import { Icons } from "@/components/ui/icons";
import type { MusicFields } from "./music-content";
import { MusicPlayer } from "./music-player";

/**
 * A track (mockup `.mu`), the card and its opened window: artwork colours
 * behind the cover, title and artist, and a play mark. Opened, the Spotify
 * player takes the mark's place when the track has one.
 */
export function MusicFace({
  fields,
  density,
  paint,
  opened = false,
  testIdPrefix,
}: {
  fields: MusicFields;
  density: CardPreviewDensity;
  /** The card's paint; an opened face wears its frame's. */
  paint?: string;
  opened?: boolean;
  /** The card's test id (`music-card`), the prefix of its parts' ids. */
  testIdPrefix: string;
}) {
  const { track, artist, embedId } = fields;
  return (
    <GadgetCard
      paint={paint}
      density={density}
      className={opened ? OPENED_PART : undefined}
      data-testid={opened ? `${testIdPrefix}-face` : testIdPrefix}
    >
      <div className="flex min-w-0 items-center gap-[0.8em]">
        <span
          aria-hidden="true"
          className="h-[3.4em] w-[3.4em] shrink-0 rounded-[0.7em] shadow-[0_6px_16px_rgba(0,0,0,0.3)]"
          style={{ background: "var(--gadget-music-art)" }}
        />
        <span className="min-w-0">
          <span
            className="block truncate font-semibold"
            data-testid={`${testIdPrefix}-track`}
          >
            {track}
          </span>
          <span
            className="block truncate text-[0.86em] text-[color:var(--gadget-ink-soft)]"
            data-testid={`${testIdPrefix}-artist`}
          >
            {artist}
          </span>
        </span>
      </div>
      {opened && embedId ? (
        <MusicPlayer embedId={embedId} track={track} artist={artist} />
      ) : (
        <span
          aria-hidden="true"
          className="flex h-[2.8em] w-[2.8em] items-center justify-center self-center rounded-full bg-[var(--gadget-track)]"
        >
          <Icons.Play className="ml-[0.1em] h-[1.2em] w-[1.2em] fill-current" />
        </span>
      )}
    </GadgetCard>
  );
}
